import type {
  EventsOverview,
  LifecycleOverview,
  SignupOverview,
  UsageOverview,
  WorkspacePlan,
  WorkspaceUsageRow,
} from '@floorpoll/shared';
import { apiRequest } from './api.js';

/**
 * The operator endpoints. Named `usage` to match the route the user sees;
 * the API paths stay `/admin/*`, which is what they are.
 */
export const usageApi = {
  overview: (windowDays: number) =>
    apiRequest<{ overview: UsageOverview }>(`/admin/overview?window=${windowDays}`).then(
      (r) => r.overview,
    ),

  workspaces: () =>
    apiRequest<{ workspaces: WorkspaceUsageRow[] }>('/admin/workspaces').then((r) => r.workspaces),

  /** `days` is clamped server-side to 7-120 — a daily chart, not the KPI window. */
  events: (days: number) =>
    apiRequest<{ events: EventsOverview }>(`/admin/events?days=${days}`).then((r) => r.events),

  /** Lifecycle email sends, opt-outs and failures. Same day clamp as `events`. */
  lifecycle: (days: number) =>
    apiRequest<{ lifecycle: LifecycleOverview }>(`/admin/lifecycle?days=${days}`).then(
      (r) => r.lifecycle,
    ),

  /** Which channel accounts arrived through. Same day clamp as `events`. */
  signups: (days: number) =>
    apiRequest<{ signups: SignupOverview }>(`/admin/signups?days=${days}`).then((r) => r.signups),

  /**
   * Puts a workspace on Marquee with no payment behind it.
   *
   * Returns the plan the server actually stored, taken from the write itself
   * rather than a read issued after it — this table's reads are eventually
   * consistent, so a read straight after the write can come back with the item
   * as it was *before*, and the operator would see Free a moment after
   * granting Marquee.
   */
  grantPlan: (workspaceId: string) =>
    apiRequest<{ plan: WorkspacePlan }>(`/admin/workspaces/${workspaceId}/grant`, {
      method: 'POST',
    }).then((r) => r.plan),

  /** Removes a grant. Never touches what Stripe paid for. */
  revokePlan: (workspaceId: string) =>
    apiRequest<{ plan: WorkspacePlan }>(`/admin/workspaces/${workspaceId}/grant`, {
      method: 'DELETE',
    }).then((r) => r.plan),
};
